import { useState, useRef, useEffect } from 'react';
import { getShopConfiguration } from '../api';
import { useGlobalContext } from './GlobalContext';

interface SubDetailProps {
  forceUpdateError: boolean;
  isLoadingNewPaymentMethod: boolean;
  isLoadingOrderNow: boolean;
  isLoadingReactivate: boolean;
  isLoadingShippingRate: boolean;
  isMerchantBannerOpen: boolean;
  isNewPaymentSaved: boolean;
  isUpdatingShuffleProduct: boolean;
  modalOpen: boolean;
  nextBAR: React.MutableRefObject<SubscriptionBillingAttemptRetry | undefined>;
  originOrder: OriginOrder | null;
  paymentResponse: string | null;
  productImages: ProductImagesCollection[] | null;
  sellingPlans: any[] | null;
  sendEmailIfAddPaymentFails: boolean;
  setDisableOpenButton: React.Dispatch<React.SetStateAction<boolean>>;
  setForceUpdateError: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingNewPaymentMethod: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingOrderNow: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingReactivate: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingShippingRate: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMerchantBannerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsNewPaymentSaved: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUpdatingShuffleProduct: React.Dispatch<React.SetStateAction<boolean>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOriginOrder: React.Dispatch<React.SetStateAction<OriginOrder | null>>;
  setPaymentResponse: React.Dispatch<React.SetStateAction<string | null>>;
  setProductImages: React.Dispatch<
    React.SetStateAction<ProductImagesCollection[] | null>
  >;
  setSellingPlans: React.Dispatch<React.SetStateAction<any[] | null>>;
  setSendEmailIfAddPaymentFails: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPauseModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowReactivateModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSubscriptionIsInDunning: React.Dispatch<React.SetStateAction<boolean>>;
  showPauseModal: boolean;
  showReactivateModal: boolean;
  subscriptionIsInDunning: boolean;
  subscriptionQuantityChanges: React.MutableRefObject<Subscription | undefined>;
}

export const useSubDetail = (): SubDetailProps => {
  const [modalOpen, setModalOpen] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [sellingPlans, setSellingPlans] = useState<any[] | null>();
  const subscriptionQuantityChanges = useRef<Subscription>();
  const [, setDisableOpenButton] = useState(false);
  const [isNewPaymentSaved, setIsNewPaymentSaved] = useState(false);
  const [isLoadingReactivate, setIsLoadingReactivate] = useState(false);
  const [isLoadingShippingRate, setIsLoadingShippingRate] = useState(false);
  const [isMerchantBannerOpen, setIsMerchantBannerOpen] = useState(true);
  const [isUpdatingShuffleProduct, setIsUpdatingShuffleProduct] =
    useState(false);
  const [isLoadingNewPaymentMethod, setIsLoadingNewPaymentMethod] =
    useState(false);
  const [productImages, setProductImages] = useState<
    ProductImagesCollection[] | null
  >();

  const [sendEmailIfAddPaymentFails, setSendEmailIfAddPaymentFails] =
    useState(false);
  const [paymentResponse, setPaymentResponse] = useState<string | null>(null);
  const [subscriptionIsInDunning, setSubscriptionIsInDunning] = useState(false);
  const [forceUpdateError, setForceUpdateError] = useState(false);
  const [originOrder, setOriginOrder] = useState<OriginOrder | null>(null);
  const nextBAR = useRef<SubscriptionBillingAttemptRetry>();
  const [isLoadingOrderNow, setIsLoadingOrderNow] = useState(false);

  return {
    forceUpdateError,
    isLoadingNewPaymentMethod,
    isLoadingOrderNow,
    isLoadingReactivate,
    isLoadingShippingRate,
    isMerchantBannerOpen,
    isNewPaymentSaved,
    isUpdatingShuffleProduct,
    modalOpen,
    nextBAR,
    originOrder,
    paymentResponse,
    productImages,
    sellingPlans,
    sendEmailIfAddPaymentFails,
    setDisableOpenButton,
    setForceUpdateError,
    setIsLoadingNewPaymentMethod,
    setIsLoadingOrderNow,
    setIsLoadingReactivate,
    setIsLoadingShippingRate,
    setIsMerchantBannerOpen,
    setIsNewPaymentSaved,
    setIsUpdatingShuffleProduct,
    setModalOpen,
    setOriginOrder,
    setPaymentResponse,
    setProductImages,
    setSellingPlans,
    setSendEmailIfAddPaymentFails,
    setShowPauseModal,
    setShowReactivateModal,
    setSubscriptionIsInDunning,
    showPauseModal,
    showReactivateModal,
    subscriptionIsInDunning,
    subscriptionQuantityChanges,
  };
};

export const useShopConfiguration = (
  subscription: Subscription,
): ShopConfiguration => {
  const { customerId, authToken } = useGlobalContext();
  const [shopConfiguration, setShopConfiguration] =
    useState<ShopConfiguration>();

  useEffect(() => {
    const fetchShopConfiguration = async () => {
      try {
        const shopResponse = await getShopConfiguration(
          customerId,
          subscription.ShopDomain,
          authToken,
        );

        setShopConfiguration(shopResponse);
      } catch (e) {
        console.error(e);
      }
    };

    if (customerId && subscription && authToken && !shopConfiguration) {
      fetchShopConfiguration();
    }
  }, [subscription, authToken, customerId, shopConfiguration]);

  return shopConfiguration;
};
